import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { AppShell, type ContentWidth } from '@/components/layout/AppShell';
import { PageSkeleton } from '@/components/ui/PageSkeleton';
import { PageErrorState } from '@/components/ui/PageErrorState';
import { ProjectHero } from '@/components/project/ProjectHero';
import { useAuth } from '@/store/auth.store';
import { useReadOnlyProjectRedirect, useCrewProjectRedirect } from '@/lib/route-guards';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';
import { ActivityFeed } from '@/components/activity/ActivityFeed';

// ─── Owner Report Button (W124.2 removed from hero) ─────────────────────────
// Histórico: até o W124.1 a função "Relatório do mês" era um dropdown no
// hero da obra. Ocupava 60% do viewport junto do resto e era a única ação
// que não pertencia ao próprio shell — o relatório é uma feature de
// /entrega, não do hero. W124.2 removeu o dropdown daqui; o button foi
// migrado pra `/projects/:id/entrega?tab=relatorio` (W123.12). Se algum
// dia precisar do dropdown de novo, ver git blame deste arquivo no
// commit anterior — toda a implementação (com polling assíncrono) está
// preservada lá.


/**
 * Wave 314c — sub-routes considered "Finanças" for the scope banner. If the
 * current user is tagged Financeiro on this project AND they're outside this
 * list, we surface a soft yellow banner reminding them they're acting outside
 * their lane. Auth is unchanged.
 */
const FINANCE_SUBROUTES = new Set([
  'compras',
  'orcamento',
  'medicoes',
  'financeiro',
  'verba',
  'demonstrativo',
  'apropriacoes',
  'pagamentos',
  'cotacoes',
  'dre',
  'fluxo-caixa',
]);

/**
 * Wave 355 Run C · default export does ONE thing: read the project id
 * out of the route, run the read-only redirect guard, and either bounce
 * client/broker to `/owner/projects/:id` or render the crew shell.
 *
 * All data hooks (`useQuery`, `useState`, `useRef`, etc.) live in
 * `ProjectDetailsBody` so the conditional early-return above doesn't
 * change React's hook count between renders. Same shape as
 * AlertasPage / CarteiraPage / etc. from Wave 355 Run B.
 */
export default function ProjectDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const projectId = id!;

  // WAVE-152 (P0-C) · foreman/worker hitting /projects/:id directly (deep-link,
  // Campo back-button) must never land in the full PM shell (hero + mesas
  // Plano/Compras/Dinheiro). Bounce crew to /campo with a visible banner
  // BEFORE the project query — mirrors the client/broker boundary below.
  const crewRedirect = useCrewProjectRedirect();
  if (crewRedirect) return crewRedirect;

  // Wave 355 Run C · client/broker hitting /projects/:id directly should
  // never see the crew project shell. Redirect them to the owner-shaped
  // surface BEFORE we kick off the project query — keeps the data fetch
  // crew-only and avoids a flash of the wrong UI.
  const redirect = useReadOnlyProjectRedirect(projectId);
  if (redirect) return redirect;

  return <ProjectDetailsBody />;
}

function ProjectDetailsBody() {
  const { id } = useParams<{ id: string }>();
  const projectId = id!;
  const location = useLocation();

  const { data: project, isLoading, isError, refetch } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId),
    staleTime: 10_000,
  });

  if (isLoading) {
    return (
      <AppShell>
        <PageSkeleton />
      </AppShell>
    );
  }

  if (isError || !project) {
    return (
      <AppShell>
        <PageErrorState
          onRetry={() => refetch()}
          iconVariant="error"
        />
      </AppShell>
    );
  }

  // Wave 341 · widen the project shell on data-heavy tabs. Forms +
  // visão geral stay narrow; finance/budget/tables get a roomier
  // track on desktop. Mobile is identical across modes.
  //
  // Wave 376b (BACKLOG-UX · UI-WIDTH-PROJECT-SUBVIEWS, 2026-05-02) —
  // INVERSION. Every sub-view defaults to `wide`; only routes explicitly
  // listed in `narrowTabs` opt out (none today).
  const widePathSegment = location.pathname.split('/').pop() ?? '';
  const narrowTabs = new Set<string>([]);
  const projectShellWidth: ContentWidth = narrowTabs.has(widePathSegment) ? 'standard' : 'wide';

  // W125.2 · 2026-05-26 · BUG B-3 fix — ProjectTabBar (W349/W350) removido.
  //
  // Histórico: até o W124.5 o `ProjectTabBar` renderizava aqui um pin/dynamic
  // tab strip (alwaysOn "Visão geral" + pinned + 2 dynamic + botão "Tudo")
  // que aparecia em TODAS as rotas project-scope. Com a chegada das mesas
  // novas (/hoje · /plano · /compras · /obra · /dinheiro · /entrega — W123.x)
  // o tab strip virou redundante e conflitante:
  //   • "Obra Hoje" do strip apontava pra /obra-hoje (rota legada) enquanto
  //     a nova mesa /hoje virou o landing
  //   • "Visão geral" levava pra ProjectHomePage cockpit, mas o sidebar +
  //     TopBar dinâmico (W124.4) já entregam navegação canônica
  //   • Drift visual: duas barras de navegação concorrentes em cada mesa.
  //
  // Decisão W125.2: deletar render do strip. A função `ProjectTabBar` está
  // marcada como @deprecated mais abaixo (mantida no arquivo pra rollback
  // rápido; ela não é mais referenciada em runtime).
  //
  // O index `/projects/:id` redireciona pra `/hoje` no router (W125.2) —
  // o landing é a mesa Hoje, não mais o cockpit ProjectHomePage.
  return (
    <AppShell contentWidth={projectShellWidth}>
      <ProjectHero
        projectId={projectId}
        name={project.name}
        status={project.status}
        address={project.address ?? null}
        // W126.1 · 2026-05-26 — passa clientName/tipoObra pro hero
        // detectar fallback "A definir" do wizard finalize e renderizar
        // MissingFieldBadge.
        clientName={project.clientName ?? null}
        tipoObra={project.tipoObra ?? null}
      />

      <div className="mt-4">
        <ScopeBanner projectId={projectId} />

        <Outlet context={{ projectId }} />

        {/* Wave FFFFF · Atividade recente widget — shown only on the
            /visao-geral path (W125.2: deixou de renderizar no index porque
            o index agora redireciona pra /hoje). */}
        <RecentActivityWidget projectId={projectId} />
      </div>
    </AppShell>
  );
}

// ─── Recent Activity Widget ───────────────────────────────────────────────────
// Small card surfacing the 5 latest activity items. Only visible on the
// project home path so it doesn't add noise to sub-pages.

function RecentActivityWidget({ projectId }: { projectId: string }) {
  const { t } = useT();
  const location = useLocation();
  const pt = t.projectActivity;

  // Wave D: cockpit is now the root; activity widget moves to /visao-geral only.
  // The root (/projects/:id) is now the ObraCockpit which already surfaces
  // operational info — the activity feed would be redundant there.
  const pathEnd = location.pathname.replace(/\/$/, '').split('/').pop() ?? '';
  const isVisaoGeral = pathEnd === 'visao-geral';
  if (!isVisaoGeral) return null;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, isLoading, isError } = useQuery({
    queryKey: ['project-activity-widget', projectId],
    queryFn: () => api.listProjectActivity(projectId, { limit: 5, sinceDays: 30 }),
    staleTime: 30_000,
  });

  return (
    <div className="mt-6 rounded-xl border border-line bg-surface px-4 pb-4 pt-3">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-[13.5px] font-semibold text-ink">{pt.widgetTitle}</h3>
        <Link
          to={`/projects/${projectId}/atividade`}
          className="text-[12px] font-medium text-ink-muted hover:text-ink transition-colors"
        >
          {pt.widgetViewAll} →
        </Link>
      </div>
      <ActivityFeed
        items={data?.items ?? []}
        isLoading={isLoading}
        isError={isError}
        maxItems={5}
        skeletonCount={3}
      />
    </div>
  );
}

/**
 * Wave 314c — yellow banner shown when the current user is tagged
 * `scope_label='financeiro'` on this project AND they're not on a
 * Finance sub-route. Cheap to compute: reuses the project members
 * query react-query already cached by MembersPage when applicable;
 * falls through to its own fetch otherwise.
 */
function ScopeBanner({ projectId }: { projectId: string }) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const user = useAuth((s) => s.user);
  const location = useLocation();

  // Last segment after /projects/:id/ — if missing, we're on Visão Geral.
  const sub =
    location.pathname.replace(/\/+$/, '').split('/').pop() ?? '';
  const isFinanceSubroute = FINANCE_SUBROUTES.has(sub);

  // Skip the fetch if we're already on a finance page or no user.
  const enabled = Boolean(user && !isFinanceSubroute);
  const membersQuery = useQuery({
    queryKey: ['project-members', projectId],
    queryFn: () => api.listMembers(projectId),
    enabled,
    staleTime: 30_000,
  });

  if (!enabled) return null;
  const me = membersQuery.data?.find((m) => m.userId === user!.id);
  if (!me || me.scopeLabel !== 'financeiro') return null;

  return (
    <div
      role="status"
      className="mb-4 flex items-start gap-2 rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-[12px] leading-snug text-ink"
    >
      <span aria-hidden className="mt-px text-warn">⚠</span>
      <p>
        {isEn ? (
          <>You are assigned as <strong>Finance</strong> on this project. Changes outside Finance (purchases, measurements, budget, P&L, etc.) are logged in the project history.</>
        ) : (
          <>Você está marcado como <strong>Financeiro</strong> nessa obra. Alterações fora de Finanças (Compras, Medições, Verba, DRE, etc.) ficam registradas no histórico do projeto.</>
        )}
      </p>
    </div>
  );
}

// Wave 315 · split-easing transition para Tabs.
//
// emil flag: ease-out-soft em mudança de cor/borda altera a curva de
// saturação. Ease plain é o feel certo pra hover/active-state shifts;
// ease-out-soft fica reservado pro press transform.
//
// Tailwind não permite combinar duas transition rules numa class só, então
// uso `style` literal — pequena exceção controlada à regra "tudo via classe".
const TAB_TRANSITION_STYLE = {
  transition:
    'color 150ms ease, border-color 150ms ease, transform 100ms var(--ease-out-soft)',
};

function Tab({
  to,
  end,
  children,
  icon,
  density = 'default',
}: {
  to: string;
  end?: boolean;
  children: ReactNode;
  icon?: TabIcon;
  density?: 'default' | 'compact' | 'iconLabel';
}) {
  return (
    <NavLink
      to={to}
      end={end}
      style={TAB_TRANSITION_STYLE}
      // Wave 320 · blur após click — evita ring de focus persistir em pill-shape.
      onMouseDown={(e) => {
        const target = e.currentTarget;
        requestAnimationFrame(() => target.blur());
      }}
      className={({ isActive }) => {
        // Wave 334K · 3 density variants. iconLabel é o novo default da
        // tab bar de projeto; compact/default ficam pra lugares legacy
        // que ainda usam SortableTabs.
        const base =
          'relative inline-flex items-center justify-center whitespace-nowrap font-medium ' +
          'transition-colors duration-150 ease ' +
          'active:scale-[0.98] outline-none focus-visible:bg-brand-400/8';

        if (density === 'iconLabel') {
          // Lifted card style: pill com fundo na ativa, texto-only inativo.
          // shadow-inset border vence sobre `border` porque não muda o box
          // size (o card não pula 1px ao virar ativo).
          return cn(
            base,
            'gap-2 rounded-md h-10 min-w-[112px] flex-1 px-3 text-[13.5px]',
            isActive
              ? 'bg-surface text-ink shadow-[inset_0_0_0_1px_rgb(var(--brand-400)/0.4)]'
              : 'text-ink-muted hover:bg-surface hover:text-ink',
          );
        }
        // legacy underline styles
        const sizing =
          density === 'compact'
            ? 'h-9 px-2.5 text-[12.5px]'
            : 'h-11 px-3 text-sm';
        return cn(
          base,
          sizing,
          'border-b-2 -mb-px',
          isActive
            ? 'border-brand-400 text-ink'
            : 'border-transparent text-ink-muted hover:text-ink',
        );
      }}
    >
      {density === 'iconLabel' && icon && (
        <span className="shrink-0 text-current">
          <TabIconSvg name={icon} />
        </span>
      )}
      <span className="truncate">{children}</span>
    </NavLink>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Wave 334K — ruthless tab redesign.
//
// Histórico:
//   • W295 splittou 18 abas em primary + "Mais ▾"; dropdown bugou em
//     viewport médio.
//   • W334I tentou two-row tabs; ficou feio (tamanhos diferentes nas
//     linhas, sem coerência visual).
//
// Diagnóstico do JP (2026-04-30): "ta feio hoje pq nao tem coerencia
// visual." E ele tá certo. 19 abas de tamanhos variáveis com hierarquia
// confusa não dá pra resolver com layout — só com edição de conteúdo.
//
// Decisão: 7 abas, fim. Mesma altura, mesma fonte, mesmo padding,
// ícone+label. Fixed `min-w-[112px]` pra todos lerem visualmente como
// chunks iguais. Tudo que sobra (12 áreas) vive no card "Outras áreas"
// dentro de Visão Geral — tile grid grande, hierarquia clara.
//
// As 7 escolhidas seguem frequência observada do JP:
//   1. Visão geral (home)         — toda visita
//   2. Etapas                     — contexto da obra
//   3. Diário                     — diário, RDOs
//   4. Calendário                 — agenda compromisso
//   5. Compras                    — POs, foco recorrente
//   6. Medições                   — financeiro mensal
//   7. Fotos                      — registro visual
//
// Drag-to-reorder mantido (SortableTabs continua plugado). 7 abas
// com nomes curtos cabem em viewport >= 720px; abaixo disso scroll
// horizontal — overflow-x-auto continua aplicado.
// ────────────────────────────────────────────────────────────────────────────

type TabIcon =
  | 'home' | 'list' | 'book' | 'calendar' | 'cart' | 'chart' | 'camera';

type TabDef = {
  id?: string;
  to: string;
  label: string;
  icon?: TabIcon;
  end?: boolean;
};

function TabIconSvg({ name }: { name: TabIcon }) {
  // 18×18 strokeWidth 1.6 — emil rule: ícones lêem como peso de texto,
  // não como decoração. Stroke fino, currentColor pra herdar do label.
  const props = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  switch (name) {
    case 'home':
      return (
        <svg {...props}>
          <path d="M4 11l8-7 8 7M6 10v9a1 1 0 001 1h3v-6h4v6h3a1 1 0 001-1v-9" />
        </svg>
      );
    case 'list':
      return (
        <svg {...props}>
          <path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" />
        </svg>
      );
    case 'book':
      return (
        <svg {...props}>
          <path d="M4 5.5A2.5 2.5 0 016.5 3H19v16H6.5A2.5 2.5 0 014 16.5v-11zM8 7h8M8 11h8M8 15h5" />
        </svg>
      );
    case 'calendar':
      return (
        <svg {...props}>
          <path d="M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1zM4 10h16M8 3v4M16 3v4" />
        </svg>
      );
    case 'cart':
      return (
        <svg {...props}>
          <path d="M3 4h2l2.5 11a2 2 0 002 1.5H17a2 2 0 002-1.5L21 7H6" />
          <circle cx="9" cy="20" r="1" />
          <circle cx="17" cy="20" r="1" />
        </svg>
      );
    case 'chart':
      return (
        <svg {...props}>
          <path d="M4 19V5M4 19h16M8 15v-4M12 15V8M16 15v-6" />
        </svg>
      );
    case 'camera':
      return (
        <svg {...props}>
          <path d="M5 7h3l2-2h4l2 2h3a1 1 0 011 1v10a1 1 0 01-1 1H5a1 1 0 01-1-1V8a1 1 0 011-1z" />
          <circle cx="12" cy="13" r="3.5" />
        </svg>
      );
  }
}

/**
 * Wave 349 · single registry of every project area.
 *
 * Was: 7 hard-coded "default" tabs + a separate lâmina list of "extras".
 * Now: ONE list. The tab bar derives what to show; the lâmina shows
 * everything with a pin toggle.
 *
 * Selection rules (in `useTabSelection`):
 *   1. If the user pinned anything → show pinned items as tabs.
 *      "Visão geral" (home) is always-on, can't be unpinned (it's the
 *      project landing page).
 *   2. If the user has zero pins → auto-fill the tab bar with the
 *      top-N most-used areas tracked in localStorage. JP described
 *      this as "deixar na tab o que for mais usado pelo usuário".
 *   3. The lâmina contains *every* area regardless. Pinning from the
 *      lâmina animates the area into the tab bar (framer-motion
 *      shared-layout).
 *
 * The button label changed: "Mais áreas" → "Tudo" — JP feedback was
 * that the old name suggested "more = secondary" when in fact the
 * lâmina is now the canonical entry to *every* area.
 */
type AreaCategory = 'principal' | 'qualidade' | 'financeiro' | 'cronograma' | 'pessoas' | 'documentos';

interface AreaDef {
  id: string;
  to: (projectId: string) => string;
  label: string;
  icon: TabIcon;
  category: AreaCategory;
  hint: string;
  /** When true, this area can never be unpinned (always in tab bar). */
  alwaysOn?: boolean;
  /** Pass-through for NavLink end matcher. */
  end?: boolean;
}

const ALL_PROJECT_AREAS: AreaDef[] = [
  // Principal — always include `home`. The other "core 7" from before
  // (etapas, logs, calendar, purchases, finance, photos) are now
  // optional pins, not hardcoded.
  { id: 'home',       to: (p) => `/projects/${p}`,            label: 'Visão geral',     icon: 'home',     category: 'principal', hint: 'Pulso do projeto · números do dia', alwaysOn: true, end: true },
  // Obra Hoje · PM's single-page operating queue — what's late, what to
  // approve, what to buy, who is blocked, and what changed since yesterday.
  // Near the top of principal so it's always discoverable in the lâmina.
  // W156 · 2026-06-01 — `/obra-hoje` virou redirect pra `/hoje` (canônica).
  // Catálogo aponta direto pra evitar o hop e o banner de redirect.
  { id: 'obra-hoje',  to: (p) => `/projects/${p}/hoje`,  label: 'Hoje',       icon: 'list',     category: 'principal', hint: 'O que fazer hoje · próxima ação e pendências' },
  { id: 'etapas',    to: (p) => `/projects/${p}/etapas`,    label: 'Etapas',          icon: 'list',     category: 'principal', hint: 'Cronograma físico-financeiro' },
  { id: 'logs',      to: (p) => `/projects/${p}/logs`,      label: 'Diário de obra',  icon: 'book',     category: 'principal', hint: 'RDOs do projeto' },
  // Wave 379BN · #87 fatia A · /calendar redireciona pra timeline?view=calendar.
  // Nav aponta direto pra evitar 2 hops.
  { id: 'calendar',  to: (p) => `/projects/${p}/timeline?view=calendar`,  label: 'Calendário',      icon: 'calendar', category: 'cronograma', hint: 'Marcos, eventos, feriados' },
  // C11 · #132 · entrada Gantt no menu (Wave 379BN consolidou Cronograma em
  // /timeline?view=gantt mas o menu não tinha link). Label='Gantt' (não
  // 'Cronograma') pra não colidir com o header da categoria 'cronograma' na
  // lâmina; o TimeViewSwitcher in-page mantém 'Cronograma' como chip label.
  { id: 'gantt',     to: (p) => `/projects/${p}/timeline?view=gantt`,     label: 'Gantt',           icon: 'chart',    category: 'cronograma', hint: 'Etapas com prazos · Gantt' },
  // Wave 379U · "Compras" aponta pro Kanban (/compras). /purchases
  // continua acessível por URL antiga; nav vai pra hub Backlog→Cotação→PO.
  { id: 'purchases', to: (p) => `/projects/${p}/compras`,   label: 'Compras',         icon: 'cart',     category: 'financeiro', hint: 'Itens → Cotação → Pedido' },
  { id: 'finance',   to: (p) => `/projects/${p}/finance`,   label: 'Financeiro',      icon: 'chart',    category: 'financeiro', hint: 'Fluxo, DRE, retidos' },
  // Wave merge-photos-fotos · 2026-05-21
  // `/photos` foi consolidado em `/fotos` (single entry, two visions). O
  // link legado vira `RedirectWithToast` no router; o catálogo TUDO da obra
  // mostra só uma entrada — "Fotos" — apontando pra galeria canônica.
  { id: 'fotos',     to: (p) => `/projects/${p}/fotos`,     label: 'Fotos',           icon: 'camera',   category: 'principal', hint: 'Álbuns e galeria com filtros' },
  // Qualidade
  { id: 'fvs',       to: (p) => `/projects/${p}/fvs`,       label: 'FVS',             icon: 'list',     category: 'qualidade',  hint: 'Verificação de serviços' },
  { id: 'rnc',       to: (p) => `/projects/${p}/rnc`,       label: 'RNCs',            icon: 'list',     category: 'qualidade',  hint: 'Não-conformidades' },
  { id: 'issues',    to: (p) => `/projects/${p}/issues`,    label: 'Problemas',       icon: 'list',     category: 'qualidade',  hint: 'Pendências em geral' },
  // Financeiro extra
  { id: 'orcamento',     to: (p) => `/projects/${p}/orcamento`,     label: 'Orçamento',  icon: 'chart', category: 'financeiro', hint: 'BDI, composições, verba' },
  // 2026-05-12 · entrada Trena na lâmina "TUDO" da obra
  { id: 'trena',         to: (p) => `/projects/${p}/trena`,         label: 'Trena',      icon: 'chart', category: 'financeiro', hint: 'Orçamentar a partir dos documentos' },
  { id: 'curva-abc',     to: (p) => `/projects/${p}/curva-abc`,     label: 'Curva ABC',  icon: 'chart', category: 'financeiro', hint: 'Concentração de gasto' },
  { id: 'change-orders', to: (p) => `/projects/${p}/change-orders`, label: 'Aditivos',   icon: 'chart', category: 'financeiro', hint: 'Mudanças de escopo' },
  // Cronograma extra
  { id: 'timeline',  to: (p) => `/projects/${p}/timeline`,  label: 'Linha do tempo',  icon: 'calendar', category: 'cronograma', hint: 'Eventos da obra em ordem' },
  { id: 'entrega',   to: (p) => `/projects/${p}/entrega`,   label: 'Entrega',         icon: 'list',     category: 'cronograma', hint: 'Punch list final' },
  // Pessoas
  { id: 'mao-de-obra', to: (p) => `/projects/${p}/mao-de-obra`, label: 'Mão-de-obra', icon: 'list', category: 'pessoas', hint: 'Operários e produtividade' },
  { id: 'members',     to: (p) => `/projects/${p}/members`,     label: 'Membros',     icon: 'list', category: 'pessoas', hint: 'Quem tem acesso' },
  // Documentos
  { id: 'docs',      to: (p) => `/projects/${p}/docs`,      label: 'Documentos',   icon: 'book', category: 'documentos', hint: 'ART, alvarás, contratos' },
  // Wave merge-activity-audit · 2026-05-21 · Atividade absorveu Auditoria
  // como tab (`?view=audit`). O link "Auditoria" do TUDO aponta direto
  // para a tab canônica em vez de cair no `RedirectWithToast` legado.
  { id: 'audit',     to: (p) => `/projects/${p}/atividade?view=audit`, label: 'Auditoria',    icon: 'list', category: 'documentos', hint: 'Trilha técnica · hash-encadeada' },
  // Wave FFFFF · Project activity feed.
  { id: 'atividade', to: (p) => `/projects/${p}/atividade`, label: 'Atividade',    icon: 'list', category: 'documentos', hint: 'Histórico cronológico da obra' },
];

// EN variants for ALL_PROJECT_AREAS labels and hints (deprecated ProjectTabBar only).
// Keep in sync with ALL_PROJECT_AREAS order if areas are added.
const ALL_PROJECT_AREAS_EN: Record<string, { label: string; hint: string }> = {
  'home':         { label: 'Overview',      hint: 'Project pulse · daily numbers' },
  'obra-hoje':    { label: 'Today',         hint: "What to do today · next action and open items" },
  'etapas':       { label: 'Stages',        hint: 'Physical-financial schedule' },
  'logs':         { label: 'Site journal',  hint: 'Daily reports' },
  'calendar':     { label: 'Calendar',      hint: 'Milestones, events, holidays' },
  'gantt':        { label: 'Gantt',         hint: 'Stages with deadlines · Gantt' },
  'purchases':    { label: 'Purchases',     hint: 'Items → Quote → Order' },
  'finance':      { label: 'Finance',       hint: 'Cash flow, P&L, retentions' },
  'fotos':        { label: 'Photos',        hint: 'Albums and gallery with filters' },
  'fvs':          { label: 'FVS',           hint: 'Service verification' },
  'rnc':          { label: 'NCRs',          hint: 'Non-conformances' },
  'issues':       { label: 'Issues',        hint: 'Open items' },
  'orcamento':    { label: 'Budget',        hint: 'BDI, compositions, allowance' },
  'trena':        { label: 'Trena',         hint: 'Estimate from documents' },
  'curva-abc':    { label: 'ABC curve',     hint: 'Spend concentration' },
  'change-orders':{ label: 'Change orders', hint: 'Scope changes' },
  'timeline':     { label: 'Timeline',      hint: 'Project events in order' },
  'entrega':      { label: 'Delivery',      hint: 'Final punch list' },
  'mao-de-obra':  { label: 'Labor',         hint: 'Workers and productivity' },
  'members':      { label: 'Members',       hint: 'Who has access' },
  'docs':         { label: 'Documents',     hint: 'ART, permits, contracts' },
  'audit':        { label: 'Audit',         hint: 'Technical trail · hash-chained' },
  'atividade':    { label: 'Activity',      hint: 'Chronological project history' },
};

// Wave 350 · always reserve this many slots for the system's "most-used"
// suggestion. Independent of pinned count — JP wants both visible.
const DYNAMIC_SLOTS = 2;

/**
 * @deprecated W125.2 — substituído por sidebar + TopBar dinâmico das mesas
 * novas (/hoje · /plano · /compras · /obra · /dinheiro · /entrega, W124.4).
 *
 * Esta função NÃO é mais renderizada em runtime. Mantida no arquivo apenas
 * para rollback rápido caso o redirect /projects/:id → /hoje precise voltar
 * pro cockpit + tab strip. Para reativar:
 *   1. Re-instanciar `<ProjectTabBar projectId={projectId} />` em ProjectDetailsBody.
 *   2. Reverter o `<Navigate to="hoje" />` no router pra `<ProjectHomePage />`.
 *
 * Não deletar sem aprovação de JP — toda a lógica pin/dynamic/dismiss
 * (W349/W350) está acoplada aqui.
 */
function ProjectTabBar({ projectId }: { projectId: string }) {
  const { t } = useT();
  const location = useLocation();
  const [showLamina, setShowLamina] = useState(false);

  const [pinned, togglePin] = usePinnedAreas();
  const [dismissed, dismissArea, undismissArea] = useDismissedAreas();
  const useCounts = useAreaUseCounter(location.pathname, projectId);

  // Wave 350 · selection rule (per JP):
  //   1. alwaysOn (Visão geral) — locked first slot
  //   2. user's pinned — preserved order
  //   3. up to DYNAMIC_SLOTS top-most-used, excluding alwaysOn / pinned /
  //      dismissed. These ARE shown even when there are pins.
  // Each slot in (2) and (3) can be closed via the X on the chip:
  //   - pinned → unpin (togglePin removes it)
  //   - dynamic → dismiss (won't auto-suggest again until re-pinned)
  const { visibleAreas, dynamicIds } = useMemo(() => {
    const alwaysOnIds = ALL_PROJECT_AREAS.filter((a) => a.alwaysOn).map((a) => a.id);
    const pinnedIds = pinned.filter((id) => !alwaysOnIds.includes(id));
    const used = new Set<string>([...alwaysOnIds, ...pinnedIds]);

    const candidates = ALL_PROJECT_AREAS
      .filter((a) => !a.alwaysOn)
      .filter((a) => !used.has(a.id))
      .filter((a) => !dismissed.includes(a.id))
      .map((a) => ({ id: a.id, count: useCounts[a.id] ?? 0 }))
      // Stable ordering: highest count first, then registry order as
      // tiebreaker so the bar doesn't jitter between two equally-used
      // areas. Index lookup is O(N) but ALL_PROJECT_AREAS is tiny.
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        const ai = ALL_PROJECT_AREAS.findIndex((x) => x.id === a.id);
        const bi = ALL_PROJECT_AREAS.findIndex((x) => x.id === b.id);
        return ai - bi;
      });
    const dynamic = candidates.slice(0, DYNAMIC_SLOTS).map((x) => x.id);

    const orderedIds = [...alwaysOnIds, ...pinnedIds, ...dynamic];
    const areas = orderedIds
      .map((id) => ALL_PROJECT_AREAS.find((a) => a.id === id))
      .filter((a): a is AreaDef => Boolean(a));
    return { visibleAreas: areas, dynamicIds: new Set(dynamic) };
  }, [pinned, dismissed, useCounts]);

  // Close handler invoked by the X on a chip.
  const handleCloseTab = (areaId: string) => {
    const area = ALL_PROJECT_AREAS.find((a) => a.id === areaId);
    if (!area || area.alwaysOn) return; // never close alwaysOn
    if (pinned.includes(areaId)) {
      // user-pinned → unpin
      togglePin(areaId);
    } else if (dynamicIds.has(areaId)) {
      // system suggestion → dismiss so it stops auto-suggesting
      dismissArea(areaId);
    }
  };

  // Pinning from lâmina also lifts dismissal — explicit pin is a stronger
  // intent than a previous dismiss.
  const handleTogglePin = (id: string) => {
    if (dismissed.includes(id)) undismissArea(id);
    togglePin(id);
  };

  return (
    <nav className="mb-5" aria-label={t.projects.title}>
      {/*
        Wave 350 layout — single row, no horizontal scroll.
        - Left region: the tab bar. alwaysOn + pinned + top-2 dynamic.
          On narrow screens chips compress (icon-only fallback) before
          wrapping so we never need a scrollbar.
        - Right region: "Tudo" button. Always visible, never scrolls away.
      */}
      <div className="flex items-stretch gap-1 rounded-lg border border-line bg-surface-2 p-1">
        <div className="flex min-w-0 flex-1 flex-wrap gap-1">
          <AnimatedTabRow
            areas={visibleAreas}
            projectId={projectId}
            onClose={handleCloseTab}
            dynamicIds={dynamicIds}
            pinnedIds={new Set(pinned)}
          />
        </div>
        <div className="shrink-0 border-l border-line/60 pl-1">
          <TudoButton onClick={() => setShowLamina(true)} active={showLamina} />
        </div>
      </div>
      <SecondaryLamina
        projectId={projectId}
        open={showLamina}
        onClose={() => setShowLamina(false)}
        pinned={pinned}
        onTogglePin={handleTogglePin}
      />
    </nav>
  );
}

/**
 * Wave 349 · counter of how often each area is visited from the
 * project context. Persisted in localStorage. Auto-decays old data
 * by capping per-area count at 99 so a 50-visit early bias doesn't
 * hide a newly-favorite area forever.
 */
function useAreaUseCounter(
  pathname: string,
  projectId: string,
): Record<string, number> {
  const KEY = 'builder:areaUseCount';
  const [counts, setCounts] = useState<Record<string, number>>(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.localStorage.getItem(KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  });
  useEffect(() => {
    // Match the current pathname back to an area id. We expect
    // `/projects/<id>/<segment>` style paths; the segment is the
    // area id in our registry.
    const expectedPrefix = `/projects/${projectId}`;
    if (!pathname.startsWith(expectedPrefix)) return;
    const rest = pathname.slice(expectedPrefix.length).replace(/^\//, '').split('/')[0] ?? '';
    const id = rest === '' ? 'home' : rest;
    if (!ALL_PROJECT_AREAS.some((a) => a.id === id)) return;
    setCounts((prev) => {
      const next = { ...prev, [id]: Math.min(99, (prev[id] ?? 0) + 1) };
      try { window.localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, [pathname, projectId]);
  return counts;
}

/**
 * Wave 349 · Animated tab row. Uses framer-motion's shared `layoutId`
 * + `AnimatePresence` so a newly-pinned area slides in from the right
 * while the lâmina panel is dismissing — JP asked for "legal,
 * dinâmico e bonito". Reduced-motion users get a fade only (motion
 * lib already gates transforms when prefers-reduced-motion is set).
 */
function AnimatedTabRow({
  areas,
  projectId,
  onClose,
  dynamicIds,
  pinnedIds,
}: {
  areas: AreaDef[];
  projectId: string;
  onClose: (areaId: string) => void;
  dynamicIds: Set<string>;
  pinnedIds: Set<string>;
}) {
  return (
    <AnimatePresence initial={false}>
      {areas.map((a) => {
        const closable = !a.alwaysOn;
        const closeKind: 'unpin' | 'dismiss' | null = !closable
          ? null
          : pinnedIds.has(a.id)
            ? 'unpin'
            : dynamicIds.has(a.id)
              ? 'dismiss'
              : null;
        return (
          <motion.div
            key={a.id}
            layout
            layoutId={`tab-${a.id}`}
            initial={{ opacity: 0, scale: 0.96, x: 12 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.96, x: -12 }}
            transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
            className="contents"
          >
            <ProjectTab
              to={a.to(projectId)}
              label={a.label}
              icon={a.icon}
              end={a.end}
              onClose={closable ? () => onClose(a.id) : undefined}
              closeKind={closeKind}
            />
          </motion.div>
        );
      })}
    </AnimatePresence>
  );
}

/**
 * One tab chip. Same visual contract as the previous `SortableTabs`
 * children but without the drag-to-reorder gesture (pinning order
 * preserves user intent; drag adds little value once we have pin/most-used
 * driving selection). Keeps icon + label, NavLink active styling, and
 * the 44px-min height for thumb targets.
 */
function ProjectTab({
  to,
  label,
  icon,
  end,
  onClose,
  closeKind,
}: {
  to: string;
  label: string;
  icon: TabIcon;
  end?: boolean;
  onClose?: () => void;
  closeKind?: 'unpin' | 'dismiss' | null;
}) {
  // Wave 350 · two-element chip: NavLink for nav + sibling X button.
  // Wrapping the X inside the NavLink would inherit its active styling
  // and steal click events; sibling-with-shared-padding looks identical
  // and keeps semantics clean.
  const { locale } = useT();
  const isEn = locale === 'en';
  const closeTitle =
    closeKind === 'unpin'
      ? (isEn ? `Unpin ${label} from bar` : `Desfixar ${label} da barra`)
      : closeKind === 'dismiss'
        ? (isEn ? `Remove ${label} from bar` : `Remover ${label} da barra`)
        : '';
  return (
    <span className="group/tab relative inline-flex items-stretch">
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) =>
          cn(
            'pressable inline-flex h-11 min-h-11 min-w-11 items-center gap-2 rounded-md px-3 text-[13.5px] font-medium transition-colors duration-150 ease',
            'active:scale-[0.98] outline-none focus-visible:bg-brand-400/8',
            // Reserve space on the right for the X so the label doesn't
            // shift when X appears on hover. Only applied when closable.
            onClose && 'pr-8',
            isActive
              ? 'bg-surface text-ink shadow-[inset_0_0_0_1px_rgb(var(--brand-400)/0.4)]'
              : 'text-ink-muted hover:bg-surface hover:text-ink',
          )
        }
      >
        <span className="shrink-0 text-current" aria-hidden>
          <TabIconSvg name={icon} />
        </span>
        <span className="hidden sm:inline truncate">{label}</span>
      </NavLink>
      {onClose && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          aria-label={closeTitle}
          title={closeTitle}
          className={cn(
            'pressable-soft absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 min-h-11 min-w-11 items-center justify-center rounded-md',
            'text-ink-subtle transition-opacity duration-150 ease',
            // Always visible on touch (sm: pointer:coarse handled below)
            // and on focus; hover-only on fine pointers to keep chips
            // visually quiet.
            'opacity-0 group-hover/tab:opacity-100 focus-visible:opacity-100',
            '[@media(pointer:coarse)]:opacity-100',
            'hover:bg-surface-2 hover:text-ink',
          )}
        >
          <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      )}
    </span>
  );
}

/**
 * Wave 350 · per-user storage scope.
 *
 * Old key (`builder:pinnedAreas`) was browser-wide. JP wants tab prefs
 * to follow the user, not the device — multiple admins on the same
 * laptop should see their own pins. Keys are now `builder:tabPrefs:<userId>:pinned`
 * and `…:dismissed`. Falls back to a generic `:anon` namespace if the
 * user isn't loaded yet (rare race during boot).
 */
function useUserScopedKey(suffix: string): string {
  const userId = useAuth((s) => s.user?.id);
  return `builder:tabPrefs:${userId ?? 'anon'}:${suffix}`;
}

function readStringArray(key: string): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

function writeStringArray(key: string, value: string[]) {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
}

// Wave 350 · pinned areas, per user. Toggle: pin if absent, unpin if
// present. Pinning order is preserved (newest at the end) so the user
// sees their additions appear at the right of the existing pins.
function usePinnedAreas(): [string[], (id: string) => void] {
  const key = useUserScopedKey('pinned');
  const [ids, setIds] = useState<string[]>(() => readStringArray(key));
  // Re-load when the key changes (i.e. user logged in after mount).
  useEffect(() => { setIds(readStringArray(key)); }, [key]);
  const toggle = (id: string) => {
    setIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      writeStringArray(key, next);
      return next;
    });
  };
  return [ids, toggle];
}

// Wave 350 · areas the user explicitly removed from the dynamic
// "most-used" suggestions. Stays out until they pin it back from the
// lâmina (which calls `undismiss`) — pinning is an explicit reversal
// of the dismiss decision, so we don't need a separate UI to clear it.
function useDismissedAreas(): [string[], (id: string) => void, (id: string) => void] {
  const key = useUserScopedKey('dismissed');
  const [ids, setIds] = useState<string[]>(() => readStringArray(key));
  useEffect(() => { setIds(readStringArray(key)); }, [key]);
  const dismiss = (id: string) => {
    setIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      writeStringArray(key, next);
      return next;
    });
  };
  const undismiss = (id: string) => {
    setIds((prev) => {
      if (!prev.includes(id)) return prev;
      const next = prev.filter((x) => x !== id);
      writeStringArray(key, next);
      return next;
    });
  };
  return [ids, dismiss, undismiss];
}

// Map lamina item id → TabDef using the same icon vocabulary as core tabs.
// If a future lamina item doesn't have an icon mapping, we still surface
// it as a pinned tab using the generic 'list' icon.
function laminaItemToTabDef(itemId: string, projectId: string): TabDef | null {
  const base = `/projects/${projectId}`;
  const map: Record<string, { to: string; label: string; icon: TabIcon }> = {
    'obra-hoje':     { to: `${base}/hoje`,          label: 'Hoje',          icon: 'list' },
    'fvs':           { to: `${base}/fvs`,           label: 'FVS',           icon: 'list' },
    'rnc':           { to: `${base}/rnc`,           label: 'RNCs',          icon: 'list' },
    'issues':        { to: `${base}/issues`,        label: 'Problemas',     icon: 'list' },
    'orcamento':     { to: `${base}/orcamento`,     label: 'Orçamento',     icon: 'chart' },
    'curva-abc':     { to: `${base}/curva-abc`,     label: 'Curva ABC',     icon: 'chart' },
    'change-orders': { to: `${base}/change-orders`, label: 'Aditivos',      icon: 'chart' },
    'timeline':      { to: `${base}/timeline`,      label: 'Linha do tempo', icon: 'calendar' },
    'gantt':         { to: `${base}/timeline?view=gantt`, label: 'Gantt',     icon: 'chart' },
    'entrega':       { to: `${base}/entrega`,       label: 'Entrega',       icon: 'list' },
    'mao-de-obra':   { to: `${base}/mao-de-obra`,   label: 'Mão-de-obra',   icon: 'list' },
    'members':       { to: `${base}/members`,       label: 'Membros',       icon: 'list' },
    'docs':          { to: `${base}/docs`,          label: 'Documentos',    icon: 'book' },
    // Wave merge-activity-audit · 2026-05-21 · canonical URL.
    'audit':         { to: `${base}/atividade?view=audit`, label: 'Auditoria', icon: 'list' },
    'atividade':     { to: `${base}/atividade`,     label: 'Atividade',     icon: 'list' },
    // Wave NNNNN · galeria consolidada (paginada · filtros mês/ambiente/busca).
    'fotos':         { to: `${base}/fotos`,         label: 'Galeria',       icon: 'camera' },
  };
  const m = map[itemId];
  if (!m) return null;
  return { id: `pin:${itemId}`, to: m.to, label: m.label, icon: m.icon };
}

// ────────────────────────────────────────────────────────────────────────────
// Wave 335B · Lâmina secundária — slide-out lateral pra módulos de baixa
// frequência (qualidade, financeiro de fechamento, pessoas, documentos,
// auditoria). Substitui o ShortcutsRow ("Outras áreas") gambiarra que JP
// vetou em /projects/:id ("ficou meio gambiarra").
//
// Padrão emil:
//   • slide-in da direita, 200ms ease-out (sem bounce — usado dezenas de
//     vezes por dia, não pode ser fofo).
//   • backdrop semi-transparente clicável fecha.
//   • Esc fecha (a11y).
//   • items agrupados por domínio com hint curto pra cada — fim do "lista
//     de 12 botões soltos".
//   • clicar em um item navega + fecha (não mantém aberta atravessando
//     páginas, que causa flash de fundo escuro).
// ────────────────────────────────────────────────────────────────────────────

function TudoButton({ onClick, active }: { onClick: () => void; active: boolean }) {
  // Wave 349 · renamed from "Mais áreas" → "Tudo". The lâmina now
  // hosts every project area, not just the secondary ones; the old
  // label undersold what's behind the button.
  const { locale } = useT();
  const isEn = locale === 'en';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isEn ? 'Open all project areas' : 'Abrir todas as áreas do projeto'}
      aria-expanded={active}
      className={cn(
        'pressable relative inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-md px-3 text-[13.5px] font-medium',
        'transition-colors duration-150 ease',
        'active:scale-[0.98] outline-none focus-visible:bg-brand-400/8',
        'min-w-[80px]',
        active
          ? 'bg-surface text-ink shadow-[inset_0_0_0_1px_rgb(var(--brand-400)/0.4)]'
          : 'text-ink-muted hover:bg-surface hover:text-ink',
      )}
    >
      <span className="shrink-0 text-current" aria-hidden>
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      </span>
      <span className="truncate">{isEn ? 'All areas' : 'Tudo'}</span>
    </button>
  );
}

// Wave 349 · `LaminaItem` / `LaminaGroup` are derived from ALL_PROJECT_AREAS now;
// the lâmina is the canonical entry to every area, not a separate list.
type LaminaItem = { id: string; to: string; label: string; hint: string; alwaysOn?: boolean };
type LaminaGroup = { id: string; label: string; items: LaminaItem[] };

const CATEGORY_LABEL: Record<AreaCategory, string> = {
  principal:  'Principais',
  qualidade:  'Qualidade',
  financeiro: 'Financeiro',
  cronograma: 'Cronograma',
  pessoas:    'Pessoas',
  documentos: 'Documentos',
};

const CATEGORY_LABEL_EN: Record<AreaCategory, string> = {
  principal:  'Main',
  qualidade:  'Quality',
  financeiro: 'Finance',
  cronograma: 'Schedule',
  pessoas:    'People',
  documentos: 'Documents',
};

function buildLaminaGroups(projectId: string, isEn: boolean): LaminaGroup[] {
  // Group ALL_PROJECT_AREAS by category. Order of categories matches
  // CATEGORY_LABEL declaration order so the lâmina reads top-down
  // from "what people use daily" to "what they touch occasionally".
  const order: AreaCategory[] = ['principal', 'qualidade', 'financeiro', 'cronograma', 'pessoas', 'documentos'];
  const catLabels = isEn ? CATEGORY_LABEL_EN : CATEGORY_LABEL;
  return order
    .map<LaminaGroup>((cat) => ({
      id: cat,
      label: catLabels[cat],
      items: ALL_PROJECT_AREAS
        .filter((a) => a.category === cat)
        .map((a) => ({
          id: a.id,
          to: a.to(projectId),
          label: isEn ? (ALL_PROJECT_AREAS_EN[a.id]?.label ?? a.label) : a.label,
          hint: isEn ? (ALL_PROJECT_AREAS_EN[a.id]?.hint ?? a.hint) : a.hint,
          alwaysOn: a.alwaysOn,
        })),
    }))
    .filter((g) => g.items.length > 0);
}

function SecondaryLamina({
  projectId,
  open,
  onClose,
  pinned,
  onTogglePin,
}: {
  projectId: string;
  open: boolean;
  onClose: () => void;
  pinned: string[];
  onTogglePin: (id: string) => void;
}) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const groups = buildLaminaGroups(projectId, isEn);
  const pinnedSet = new Set(pinned);

  // Esc fecha; também trava scroll do body enquanto aberto.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={isEn ? 'All project areas' : 'Todas as áreas do projeto'}>
      {/* backdrop */}
      <button
        type="button"
        aria-label={isEn ? 'Close' : 'Fechar'}
        onClick={onClose}
        className="absolute inset-0 bg-bg/70 backdrop-blur-sm motion-safe:animate-fade-in"
      />
      {/* panel */}
      <aside
        className={cn(
          'absolute right-0 top-0 h-full w-full max-w-[420px] overflow-y-auto',
          'border-l border-line bg-surface shadow-2xl',
          'motion-safe:animate-drawer-in',
        )}
      >
        <header className="sticky top-0 flex flex-wrap items-center justify-between gap-3 border-b border-line bg-surface/95 px-5 py-3 backdrop-blur">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-ink-subtle">{isEn ? 'Project' : 'Projeto'}</p>
            <h2 className="text-base font-semibold tracking-tight text-ink">{isEn ? 'All areas' : 'Tudo'}</h2>
            <p className="mt-0.5 text-[11px] text-ink-subtle">
              {isEn ? 'Pin what you use every day · it goes to the tab bar.' : 'Fixe o que você usa no dia a dia · vai pra barra de abas.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={isEn ? 'Close' : 'Fechar'}
            className="pressable rounded-md p-1.5 text-ink-muted hover:bg-surface-2 hover:text-ink"
          >
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        <div className="px-5 py-4">
          {groups.map((group) => (
            <section key={group.id} className="mb-5 last:mb-0">
              <h3 className="mb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-ink-subtle">
                {group.label}
              </h3>
              <ul className="space-y-1">
                {group.items.map((item) => {
                  const isPinned = pinnedSet.has(item.id);
                  // Wave 349 · alwaysOn (Visão geral) renders the pin
                  // button locked-on. Pressing it doesn't unpin — it
                  // shows a hint instead. The label says "sempre" so
                  // the user knows the state is intentional, not a bug.
                  const isAlwaysOn = item.alwaysOn === true;
                  const showPin = isAlwaysOn || isPinned;
                  return (
                    <li key={item.id} className="group/item">
                      <div
                        className={cn(
                          'flex items-center gap-1 rounded-md border border-transparent transition-colors duration-150 ease',
                          'hover:border-line hover:bg-surface-2',
                          (isPinned || isAlwaysOn) && 'border-line/40 bg-surface-2/40',
                        )}
                      >
                        <Link
                          to={item.to}
                          onClick={onClose}
                          className="pressable flex flex-1 items-center justify-between gap-3 px-3 py-2.5"
                        >
                          <div className="min-w-0">
                            <p className="flex items-center gap-1.5 text-sm font-medium text-ink">
                              {item.label}
                              {isAlwaysOn ? (
                                <span className="rounded bg-ink/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.06em] text-ink-muted">
                                  {isEn ? 'always' : 'sempre'}
                                </span>
                              ) : isPinned ? (
                                <span className="rounded bg-brand-400/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.06em] text-brand-400">
                                  {isEn ? 'pinned' : 'fixado'}
                                </span>
                              ) : null}
                            </p>
                            <p className="truncate text-[12px] text-ink-muted">{item.hint}</p>
                          </div>
                          <span aria-hidden className="text-ink-subtle">
                            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                              <path d="M9 6l6 6-6 6" />
                            </svg>
                          </span>
                        </Link>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isAlwaysOn) return;     // no-op for always-on
                            onTogglePin(item.id);
                          }}
                          aria-label={
                            isAlwaysOn
                              ? (isEn ? `${item.label} always visible` : `${item.label} sempre visível`)
                              : isPinned
                                ? (isEn ? `Unpin ${item.label} from tab bar` : `Desfixar ${item.label} da barra de abas`)
                                : (isEn ? `Pin ${item.label} as tab` : `Fixar ${item.label} como aba`)
                          }
                          aria-pressed={isPinned || isAlwaysOn}
                          aria-disabled={isAlwaysOn}
                          title={isAlwaysOn ? (isEn ? 'Always visible' : 'Sempre visível') : isPinned ? (isEn ? 'Unpin from bar' : 'Desfixar da barra') : (isEn ? 'Pin as tab' : 'Fixar como aba')}
                          className={cn(
                            'pressable mr-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md transition-colors duration-150 ease',
                            isAlwaysOn
                              ? 'cursor-default text-ink-subtle opacity-60'
                              : isPinned
                                ? 'text-brand-400 hover:bg-surface-3'
                                : 'text-ink-subtle opacity-0 hover:bg-surface-3 hover:text-ink group-hover/item:opacity-100 focus-visible:opacity-100',
                          )}
                        >
                          <svg width={16} height={16} viewBox="0 0 24 24" fill={showPin ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <path d="M12 17v5M9 11l-2 2 5 0M15 11l2 2-5 0M9 11V5a3 3 0 016 0v6" />
                          </svg>
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </aside>
    </div>
  );
}

// ─── Wave 314N · Drag-to-reorder tabs (Chrome-style) ──────────────────
//
// Per emil-design rules: tabs are seen 100+/day, so the drag interaction
// has to feel invisible — fast ease-out, no bounce, drop snaps in 200ms.
// Click vs drag is disambiguated by HTML5 DnD's native 5px threshold
// (browsers don't fire dragstart until the pointer moves >= 5px from
// mousedown), so click always navigates and only intentional drags
// trigger reorder. Reduce-motion users skip the transform animation.
//
// Persistence: localStorage per browser (per-user implicitly via session).
// Future: sync to users.preferences when the team uses multiple devices.
function useTabOrder(key: string): [string[], (ids: string[]) => void] {
  const [order, setOrder] = useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? (parsed as string[]) : [];
    } catch {
      return [];
    }
  });
  const set = (ids: string[]) => {
    setOrder(ids);
    try {
      window.localStorage.setItem(key, JSON.stringify(ids));
    } catch {
      // sandboxed iframe / quota — ignore. Worst case order resets
      // next session.
    }
  };
  return [order, set];
}

/**
 * Reorder `defaults` according to `saved`. Tabs in `saved` come first
 * in saved order; tabs that exist in `defaults` but not in `saved`
 * (i.e. new tabs added by the app since the user last reordered) are
 * appended at the end so they don't vanish silently.
 */
function applyTabOrder(defaults: TabDef[], saved: string[]): TabDef[] {
  if (!saved || saved.length === 0) return defaults;
  const tabId = (t: TabDef): string => t.id ?? t.to;
  const byId = new Map(defaults.map((t) => [tabId(t), t]));
  const out: TabDef[] = [];
  const consumed = new Set<string>();
  for (const id of saved) {
    const tab = byId.get(id);
    if (tab) {
      out.push(tab);
      consumed.add(id);
    }
  }
  for (const tab of defaults) {
    if (!consumed.has(tabId(tab))) out.push(tab);
  }
  return out;
}

function SortableTabs({
  tabs,
  onReorder,
  density = 'default',
}: {
  tabs: TabDef[];
  onReorder: (ids: string[]) => void;
  /**
   * Wave 334I — secondary row tabs use 'compact' density: smaller
   * height + reduced font, so the second row reads as subordinate
   * navigation instead of competing with primary.
   * Wave 334K — primary row migrated to 'iconLabel': fixed-width
   * icon+label cards, no more underline/font-size mismatch.
   */
  density?: 'default' | 'compact' | 'iconLabel';
}) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  // Caller (primary nav) always provides id, but TabDef.id é optional pra
  // não obrigar o array secundário. Filtra defensivo aqui.
  const idOf = (t: TabDef): string => t.id ?? t.to;

  const move = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    const ids = tabs.map(idOf);
    const fromIdx = ids.indexOf(fromId);
    const toIdx = ids.indexOf(toId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = ids.slice();
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, fromId);
    onReorder(next);
  };

  return (
    <>
      {tabs.map((tab) => {
        const tid = idOf(tab);
        return (
        <div
          key={tid}
          draggable
          onDragStart={(e) => {
            setDraggingId(tid);
            e.dataTransfer.effectAllowed = 'move';
            // Some browsers require setData to actually start the drag.
            e.dataTransfer.setData('text/plain', tid);
          }}
          onDragOver={(e) => {
            if (!draggingId || draggingId === tid) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (dragOverId !== tid) setDragOverId(tid);
          }}
          onDragLeave={() => {
            if (dragOverId === tid) setDragOverId(null);
          }}
          onDrop={(e) => {
            e.preventDefault();
            const fromId = e.dataTransfer.getData('text/plain') || draggingId;
            if (fromId) move(fromId, tid);
            setDraggingId(null);
            setDragOverId(null);
          }}
          onDragEnd={() => {
            setDraggingId(null);
            setDragOverId(null);
          }}
          className={cn(
            'inline-flex motion-safe:transition-[transform,opacity] motion-safe:duration-150 motion-safe:ease-out',
            // Reduce-motion: collapse opacity to a hard step e mata o
            // scale/translate. Mantém só o opacity-40 que ainda dá
            // feedback visual de "isso está sendo arrastado" sem motion.
            draggingId === tid && 'opacity-40 motion-safe:scale-[0.97]',
            dragOverId === tid && draggingId !== tid && 'motion-safe:translate-x-[2px]',
          )}
          style={{
            cursor: draggingId === tid ? 'grabbing' : 'grab',
          }}
        >
          <Tab to={tab.to} end={tab.end} density={density} icon={tab.icon}>
            {tab.label}
          </Tab>
        </div>
        );
      })}
    </>
  );
}
